import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  DailyTeam,
  DailyTeamInput,
  TeamDailyBoard,
  TeamDailyNewsItem,
  TeamDailyNote,
  TeamDailyNoteInput,
  TeamDailyPresence,
  TeamDailyKennzahlen,
  TeamDailySegmentPeople,
  TeamDailyVerlauf,
  TeamDailyVerlaufTickets,
  Vorgangstyp,
} from "@msc/shared";

import { api } from "../lib/api.js";

/**
 * Query-Hooks der Kategorie TeamDaily. Alles lesend bis auf das Speichern der
 * Tagesnotizen — das Board schreibt in v1 nichts nach Jira.
 */

const q = (v: string) => encodeURIComponent(v);

/**
 * Die Bereiche, die dieser Nutzer im Board wählen darf.
 *
 * Bewusst **nicht** `useSegments()` (= alle Bereiche): Der Server lässt nur die
 * eigenen durch (403 `SEGMENT_OUT_OF_REACH`), und eine Auswahl, die mehr
 * anbietet als er annimmt, ist eine Sackgasse mit Fehlermeldung. Die
 * Team-Mitgliedschaft, die hier mitzählt, kennt der Client ohnehin nicht.
 */
export function useTeamDailySegments() {
  return useQuery<Array<{ id: string; name: string }>>({
    queryKey: ["teamdaily", "segments"],
    queryFn: async () =>
      (
        await api.get<{ segments: Array<{ id: string; name: string }> }>(
          "/teamdaily/segments",
        )
      ).segments,
    staleTime: 15 * 60 * 1000,
  });
}

/** Alle Personen des Bereichs — die Auswahlliste beim Zusammenstellen eines Teams. */
export function useTeamDailyTeam(segmentId: string | null) {
  return useQuery<TeamDailySegmentPeople>({
    queryKey: ["teamdaily", "team", segmentId],
    enabled: Boolean(segmentId),
    queryFn: () =>
      api.get<TeamDailySegmentPeople>(`/teamdaily/team?segmentId=${q(segmentId!)}`),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Das Tagesboard. `memberIds` übersteuert das Default-Team (leer = Default).
 * `staleTime` kurz, weil „heute gebucht" und offene Tickets tagesaktuell sind.
 */
export function useTeamDailyBoard(
  segmentId: string | null,
  date: string,
  teamId: string | null,
  bereit = true,
) {
  const team = teamId ? `&teamId=${q(teamId)}` : "";
  return useQuery<TeamDailyBoard>({
    queryKey: ["teamdaily", "board", segmentId, date, teamId],
    enabled: Boolean(segmentId) && bereit,
    queryFn: () =>
      api.get<TeamDailyBoard>(
        `/teamdaily/board?segmentId=${q(segmentId!)}&date=${q(date)}${team}`,
      ),
    staleTime: 60 * 1000,
  });
}

/**
 * Abwesenheiten des Teams ab `date` über zwei Wochen — für die Leiste „wer ist
 * wann weg" oben auf dem Board.
 */
export function useTeamDailyPresence(
  segmentId: string | null,
  date: string,
  teamId: string | null,
  bereit = true,
) {
  const team = teamId ? `&teamId=${q(teamId)}` : "";
  return useQuery<TeamDailyPresence>({
    queryKey: ["teamdaily", "presence", segmentId, date, teamId],
    enabled: Boolean(segmentId) && bereit,
    queryFn: () =>
      api.get<TeamDailyPresence>(
        `/teamdaily/presence?segmentId=${q(segmentId!)}&date=${q(date)}&days=14${team}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/* ------------------------------------------------------------ Daily-Teams */

/**
 * Die Teams eines Bereichs samt Mitgliedern, dazu `mine`: die Teams, in denen
 * der Angemeldete selbst steht. Daraus kommt die Vorauswahl beim Öffnen.
 */
export function useDailyTeams(segmentId: string | null) {
  return useQuery<{ teams: DailyTeam[]; mine: string[] }>({
    queryKey: ["teamdaily", "teams", segmentId],
    enabled: Boolean(segmentId),
    queryFn: () =>
      api.get<{ teams: DailyTeam[]; mine: string[] }>(
        `/teamdaily/teams?segmentId=${q(segmentId!)}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/** Legt ein Team an, ändert es oder löscht es — und frischt die Liste auf. */
export function useDailyTeamSchreiben(segmentId: string | null) {
  const qc = useQueryClient();
  const auffrischen = () =>
    qc.invalidateQueries({ queryKey: ["teamdaily", "teams", segmentId] });

  const anlegen = useMutation({
    mutationFn: (input: DailyTeamInput) =>
      api.post<{ team: DailyTeam }>(
        `/teamdaily/teams?segmentId=${q(segmentId!)}`,
        input,
      ),
    onSuccess: auffrischen,
  });
  const aendern = useMutation({
    mutationFn: ({ id, input }: { id: string; input: DailyTeamInput }) =>
      api.put<{ team: DailyTeam }>(
        `/teamdaily/teams/${q(id)}?segmentId=${q(segmentId!)}`,
        input,
      ),
    onSuccess: auffrischen,
  });
  const loeschen = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: boolean }>(
        `/teamdaily/teams/${q(id)}?segmentId=${q(segmentId!)}`,
      ),
    onSuccess: auffrischen,
  });
  return { anlegen, aendern, loeschen };
}

/**
 * Tagesnotizen des **Teams**. Ohne Team gibt es keinen Zettel: Bis zum
 * 2026-10-01 hingen die Notizen am Bereich, und zwei Dailys desselben
 * Bereichs schrieben übereinander (#264).
 */
export function useTeamDailyNotes(
  segmentId: string | null,
  teamId: string | null,
  date: string,
) {
  return useQuery<TeamDailyNote[]>({
    queryKey: ["teamdaily", "notes", segmentId, teamId, date],
    enabled: Boolean(segmentId) && Boolean(teamId),
    queryFn: async () => {
      const res = await api.get<{ notes: TeamDailyNote[] }>(
        `/teamdaily/notes?segmentId=${q(segmentId!)}&teamId=${q(teamId!)}&date=${q(date)}`,
      );
      return res.notes;
    },
    staleTime: 30 * 1000,
  });
}

/** Ersetzt die Tagesnotizen vollständig und aktualisiert den Cache. */
export function useReplaceNotes(
  segmentId: string | null,
  teamId: string | null,
  date: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (notes: TeamDailyNoteInput[]) => {
      const res = await api.put<{ notes: TeamDailyNote[] }>(
        `/teamdaily/notes?segmentId=${q(segmentId!)}&teamId=${q(teamId!)}&date=${q(date)}`,
        { notes },
      );
      return res.notes;
    },
    onSuccess: (notes) => {
      qc.setQueryData(["teamdaily", "notes", segmentId, teamId, date], notes);
    },
  });
}

/** Schlagzeilen des Tickers (Kunden des Bereichs + SAP/IT). */
export function useTeamDailyNews(
  segmentId: string | null,
  teamId: string | null,
  bereit = true,
) {
  const team = teamId ? `&teamId=${q(teamId)}` : "";
  return useQuery<TeamDailyNewsItem[]>({
    queryKey: ["teamdaily", "news", segmentId, teamId],
    enabled: Boolean(segmentId) && bereit,
    queryFn: async () => {
      const res = await api.get<{ items: TeamDailyNewsItem[] }>(
        `/teamdaily/news?segmentId=${q(segmentId!)}${team}`,
      );
      return res.items;
    },
    staleTime: 15 * 60 * 1000,
  });
}

function kennzahlParameter(
  segmentId: string,
  teamId: string | null,
  vorgangstyp: Vorgangstyp | null,
): string {
  const team = teamId ? `&teamId=${q(teamId)}` : "";
  const typ = vorgangstyp ? `&vorgangstyp=${q(vorgangstyp)}` : "";
  return `segmentId=${q(segmentId)}${team}${typ}`;
}

/** Die vier Zähler (live aus Jira). */
export function useTeamDailyKennzahlen(
  segmentId: string | null,
  teamId: string | null,
  vorgangstyp: Vorgangstyp | null,
  bereit = true,
) {
  return useQuery<TeamDailyKennzahlen>({
    queryKey: ["teamdaily", "kennzahlen", segmentId, teamId, vorgangstyp],
    enabled: Boolean(segmentId) && bereit,
    queryFn: () =>
      api.get<TeamDailyKennzahlen>(
        `/teamdaily/kennzahlen?${kennzahlParameter(segmentId!, teamId, vorgangstyp)}`,
      ),
    staleTime: 60 * 1000,
  });
}

/** Created vs. Resolved, 30 Tage, aus dem Spiegel. */
export function useTeamDailyVerlauf(
  segmentId: string | null,
  teamId: string | null,
  vorgangstyp: Vorgangstyp | null,
  bereit = true,
) {
  return useQuery<TeamDailyVerlauf>({
    queryKey: ["teamdaily", "verlauf", segmentId, teamId, vorgangstyp],
    enabled: Boolean(segmentId) && bereit,
    queryFn: () =>
      api.get<TeamDailyVerlauf>(
        `/teamdaily/verlauf?${kennzahlParameter(segmentId!, teamId, vorgangstyp)}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/** Die Tickets hinter einem Tagesbalken; lädt erst, wenn ein Tag gewählt ist. */
export function useTeamDailyVerlaufTickets(
  segmentId: string | null,
  teamId: string | null,
  vorgangstyp: Vorgangstyp | null,
  tag: string | null,
) {
  return useQuery<TeamDailyVerlaufTickets>({
    queryKey: ["teamdaily", "verlauf", "tickets", segmentId, teamId, vorgangstyp, tag],
    enabled: Boolean(segmentId) && Boolean(tag),
    queryFn: () =>
      api.get<TeamDailyVerlaufTickets>(
        `/teamdaily/verlauf/tickets?${kennzahlParameter(segmentId!, teamId, vorgangstyp)}&tag=${q(tag!)}`,
      ),
  });
}
